import { jsPDF } from 'jspdf';
import { CLP, fmtFecha } from '../utils/finance';
import { normalizeBranch, loadLogos, renderStandardHeader, renderStandardFooter } from './pdfCommon';

const C = { primary: [26, 58, 92], text: [30, 30, 30], muted: [110, 120, 130], light: [244, 246, 248], border: [220, 225, 230], white: [255, 255, 255] };

export async function generatePresupuestoPDF(pres = {}, opts = {}) {
  const autoPrint = opts.autoPrint !== false;
  const branch = normalizeBranch(pres.branch || opts.branch);
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pw = 210, margin = 16, cw = pw - margin * 2;

  const logos = await loadLogos();
  const fecha = pres.createdAt ? fmtFecha(pres.createdAt) : new Date().toLocaleDateString('es-CL');
  renderStandardHeader(doc, branch, logos, {
    title: 'PRESUPUESTO',
    number: pres.numero || 'S/N',
    date: fecha,
  });

  let y = 40;
  doc.setTextColor(...C.text); doc.setFontSize(9); doc.setFont('helvetica', 'bold');
  doc.text('Paciente', margin, y); doc.text('Propietario', margin + cw / 2, y);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5); doc.setTextColor(...C.muted);
  doc.text(`${pres.patientName || '–'}${pres.patientSpecies ? ` · ${pres.patientSpecies}` : ''}`, margin, y + 5);
  doc.text(`${pres.ownerName || '–'}${pres.ownerPhone ? ` · ${pres.ownerPhone}` : ''}`, margin + cw / 2, y + 5);
  doc.text(`Veterinario: ${pres.vetName || '–'}`, margin, y + 10);
  doc.text(`Válido hasta: ${fmtFecha(pres.validoHasta)}`, margin + cw / 2, y + 10);
  y += 18;

  // Tabla de prestaciones agrupadas por tipo
  const items = (pres.items || []).slice();
  items.sort((a, b) => {
    const tipoA = (a.tipoPrestacion || '').toLowerCase();
    const tipoB = (b.tipoPrestacion || '').toLowerCase();
    if (tipoA !== tipoB) return tipoA.localeCompare(tipoB);
    return String(a.descripcion || '').toLowerCase().localeCompare(String(b.descripcion || '').toLowerCase());
  });

  const c0 = margin;
  const wDesc = 76, wCant = 14, wPUnit = 24, wDescuento = 24, wTotal = 26;
  const gap = 2;
  const cCant = c0 + wDesc + gap;
  const cPUnit = cCant + wCant + gap;
  const cDesc = cPUnit + wPUnit + gap;
  const cTotal = cDesc + wDescuento + gap;

  const headerY = y;
  doc.setFillColor(...C.light); doc.rect(margin, headerY, cw, 7, 'F');
  doc.setTextColor(...C.primary); doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
  doc.text('Prestación', c0 + 1.5, headerY + 4.7);
  doc.text('Cant.', cCant + wCant / 2, headerY + 4.7, { align: 'center' });
  doc.text('P. Unit.', cPUnit + wPUnit, headerY + 4.7, { align: 'right' });
  doc.text('Desc.', cDesc + wDescuento, headerY + 4.7, { align: 'right' });
  doc.text('Total', cTotal + wTotal, headerY + 4.7, { align: 'right' });
  y += 7;

  doc.setFont('helvetica', 'normal'); doc.setTextColor(...C.text);

  let currentTipo = null;
  items.forEach((it, idx) => {
    const tipo = it.tipoPrestacion || 'Sin categoría';
    if (tipo !== currentTipo) {
      currentTipo = tipo;
      y += 2;
      doc.setFillColor(...C.primary); doc.rect(margin, y, cw, 6, 'F');
      doc.setTextColor(...C.white); doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.text(tipo.toUpperCase(), margin + 2, y + 4.2);
      y += 8;
      doc.setFont('helvetica', 'normal'); doc.setTextColor(...C.text); doc.setFontSize(8);
    }

    const lines = doc.splitTextToSize(String(it.prestacion || it.descripcion || '–'), wDesc - 3);
    const rowH = Math.max(6, lines.length * 4 + 2);
    if (y + rowH > 250) { doc.addPage(); y = margin; }
    if (idx % 2 === 1) { doc.setFillColor(250, 251, 252); doc.rect(margin, y, cw, rowH, 'F'); }
    doc.text(lines, c0 + 1.5, y + 4);
    doc.text(String(it.cantidad || 1), cCant + wCant / 2, y + 4, { align: 'center' });
    doc.text(CLP(it.precioUnitario), cPUnit + wPUnit, y + 4, { align: 'right' });
    doc.text(CLP(it.descuento || 0), cDesc + wDescuento, y + 4, { align: 'right' });
    doc.text(CLP(it.total), cTotal + wTotal, y + 4, { align: 'right' });
    if (it.notas) {
      y += rowH; doc.setTextColor(...C.muted); doc.setFontSize(7);
      doc.text(`  ${it.notas}`, c0 + 1.5, y + 1); doc.setFontSize(8); doc.setTextColor(...C.text); y += 2;
    } else {
      y += rowH;
    }
  });

  doc.setDrawColor(...C.border); doc.line(margin, y, margin + cw, y); y += 6;
  const totX = margin + cw - 50, valX = margin + cw;
  const row = (label, val, bold, color = C.text) => {
    doc.setFont('helvetica', bold ? 'bold' : 'normal'); doc.setFontSize(bold ? 11 : 9);
    doc.setTextColor(...color);
    doc.text(label, totX, y, { align: 'right' }); doc.text(CLP(val), valX, y, { align: 'right' });
    y += bold ? 7 : 5;
  };
  row('Neto:', Math.round((pres.total || 0) / 1.19));
  row('IVA (19%):', (pres.total || 0) - Math.round((pres.total || 0) / 1.19));
  if (pres.descuentoTotal > 0) {
    const descLabel = pres.convenioName ? `Descuento (${pres.convenioName}):` : 'Descuento:';
    row(descLabel, -pres.descuentoTotal, false, C.primary);
  }
  row('TOTAL:', pres.total, true, C.primary);

  if (pres.notas) {
    y += 4; doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(...C.text);
    doc.text('Notas', margin, y); y += 5;
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...C.muted); doc.setFontSize(8.5);
    doc.splitTextToSize(pres.notas, cw).forEach((ln) => { doc.text(ln, margin, y); y += 4.5; });
  }

  renderStandardFooter(doc, branch);

  if (autoPrint) { doc.autoPrint(); doc.output('dataurlnewwindow'); }
  return doc;
}

export default generatePresupuestoPDF;
